import React, { useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trackerService, assistantService } from "../../services/api";
import { newRequestId } from "../../utils/requestId";

const field = "block text-xs font-semibold text-[#58635d]";
const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
const time = (value, timezone) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        timeZone: timezone,
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Not recorded";
const display = (value) =>
  value === "" || value == null ? "Empty" : String(value);

function useRequest() {
  const previous = useRef(null);
  return (payload) => {
    const serialized = JSON.stringify(payload);
    if (previous.current?.serialized !== serialized)
      previous.current = { serialized, id: newRequestId() };
    return { ...payload, request_id: previous.current.id };
  };
}

export function TrackerSources() {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["trackers"],
    queryFn: trackerService.sources,
  });
  return (
    <section className="space-y-4" aria-label="Connected work trackers">
      <h2 className="text-xl font-bold">Work trackers</h2>
      <p className="text-sm text-[#68746d]">
        Review urgent bugs, delayed work, owners and observed changes in
        selected Google Sheets.
      </p>
      <Link className="secondary-button" to="/assistant?view=connections">
        Connect a tracker
      </Link>
      {isLoading && <p role="status">Loading trackers…</p>}
      {error && (
        <div role="alert">
          Trackers could not load.{" "}
          <button className="secondary-button" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}
      {data?.length === 0 && (
        <p className="surface-panel p-5">
          Add and sync a Google Sheets source in Connections, with stable IDs
          and mapped headers.
        </p>
      )}
      {data?.map((source) => (
        <Link
          key={source.id}
          className="surface-panel block space-y-2 p-5"
          to={`/trackers/${source.id}`}
        >
          <h3 className="font-bold">{source.title}</h3>
          <p className="text-sm text-[#68746d]">
            {source.error ||
              (source.synced_at
                ? `Last synced ${new Date(source.synced_at).toLocaleString()}`
                : "Not synced yet")}
          </p>
          <span className="text-sm font-semibold text-[#246b52]">
            Review work →
          </span>
        </Link>
      ))}
    </section>
  );
}

function Changes({ before, after }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr>
            <th className="p-2">Field</th>
            <th className="p-2">Before</th>
            <th className="p-2">After</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(after)
            .filter(([key, value]) => before[key] !== value)
            .map(([key, value]) => (
              <tr key={key} className="border-t border-[#e9edea]">
                <th className="p-2 font-medium">{key}</th>
                <td className="max-w-xs whitespace-pre-wrap break-words p-2">
                  {display(before[key])}
                </td>
                <td className="max-w-xs whitespace-pre-wrap break-words p-2">
                  {display(value)}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function UpdateForm({ row, busy, save, close }) {
  const [values, setValues] = useState(row.values);
  const request = useRequest();
  return (
    <form
      className="surface-panel space-y-3 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save(
          request({
            external_id: row.external_id,
            expected_revision: row.revision,
            changes: Object.fromEntries(
              row.editable_fields
                .filter((key) => values[key] !== row.values[key])
                .map((key) => [key, values[key]]),
            ),
          }),
        );
      }}
    >
      <h2 className="font-bold">
        Propose a tracker update · {row.external_id}
      </h2>
      <p className="text-sm text-[#68746d]">
        Use the values your sheet expects. This step saves a local proposal;
        applying it to Google Sheets is separate.
      </p>
      {row.editable_fields.map((key) => (
        <label className={field} key={key}>
          {key === "due"
            ? "Deadline (ISO date or timestamp with timezone)"
            : `Source ${key}`}
          <input
            className={input}
            maxLength={240}
            value={values[key]}
            onChange={(event) =>
              setValues({ ...values, [key]: event.target.value })
            }
          />
        </label>
      ))}
      <div className="flex gap-2">
        <button
          className="primary-button"
          disabled={
            busy ||
            !row.editable_fields.some((key) => values[key] !== row.values[key])
          }
        >
          Save update for review
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function FollowupForm({ row, busy, save, close }) {
  const [recipients, setRecipients] = useState("");
  const request = useRequest();
  return (
    <form
      className="surface-panel space-y-3 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save(
          request({
            expected_revision: row.revision,
            recipients: recipients
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean),
          }),
        );
      }}
    >
      <h2 className="font-bold">Prepare follow-up · {row.external_id}</h2>
      <p className="text-sm text-[#68746d]">
        The draft includes the recorded owner, status, priority, deadline and
        source link. Review its exact message in Outbox before sending.
      </p>
      <label className={field}>
        Follow-up recipients
        <input
          required
          className={input}
          value={recipients}
          placeholder="Comma-separated email addresses"
          onChange={(event) => setRecipients(event.target.value)}
        />
      </label>
      <div className="flex gap-2">
        <button disabled={busy} className="primary-button">
          Save unsent follow-up
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function PolicyForm({ initial, busy, save, close }) {
  const [values, setValues] = useState(initial);
  const [recipients, setRecipients] = useState(initial.recipients.join(", "));
  return (
    <form
      className="surface-panel space-y-3 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save({
          ...values,
          recipients: recipients
            .split(",")
            .map((value) => value.trim())
            .filter(Boolean),
        });
      }}
    >
      <h2 className="font-bold">Escalation draft settings</h2>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={values.automatic_drafts}
          onChange={(event) =>
            setValues({ ...values, automatic_drafts: event.target.checked })
          }
        />
        Prepare escalation drafts automatically
      </label>
      <p className="text-sm text-[#68746d]">
        For critical, overdue, blocked, waiting, unassigned or unchanged work.
        Uses fresh synced values, avoids repeated drafts for unchanged
        information, and cancels obsolete unsent drafts when the row changes.
        Nothing is sent automatically.
      </p>
      <label className={field}>
        Escalation recipients
        <input
          className={input}
          required={values.automatic_drafts}
          value={recipients}
          onChange={(event) => setRecipients(event.target.value)}
          placeholder="Explicit team or owner email addresses"
        />
      </label>
      <p className="text-xs text-[#68746d]">
        These recipients apply to every automatic draft from this tracker; names
        are never turned into guessed email addresses.
      </p>
      <label className={field}>
        Days without an observed tracker change
        <input
          required
          type="number"
          min={1}
          max={90}
          className={input}
          value={values.unchanged_days}
          onChange={(event) =>
            setValues({ ...values, unchanged_days: Number(event.target.value) })
          }
        />
      </label>
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          Save escalation settings
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ClassificationForm({ initial, busy, save, close }) {
  const [base] = useState(initial);
  const [values, setValues] = useState({
    status_mapping: initial.status_mapping,
    priority_mapping: initial.priority_mapping,
  });
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save({ ...values, expected_revision: base.revision });
      }}
    >
      <h2 className="font-bold">Status and priority labels</h2>
      <p className="text-sm text-[#68746d]">
        Choose how Timelyzer interprets your team’s labels. This updates alerts
        and follow-up eligibility for saved rows and future syncs. Google Sheets
        keeps its original values. Labels ignore case and surrounding spaces.
      </p>
      <p className="text-sm text-[#68746d]">
        Done and cancelled work leaves active reminders. Changed mappings retire
        unsent follow-ups and source updates so you can review them again.
        Missing rows keep their last state until a successful sync finds them.
      </p>
      {["status", "priority"].map((name) => (
        <fieldset key={name} className="space-y-2" disabled={busy}>
          <legend className="mb-2 font-semibold capitalize">{name} labels</legend>
          {base.observed[name].items.length === 0 && (
            <p className="text-sm text-[#68746d]">Sync a tracker with {name} values to configure its labels.</p>
          )}
          {base.observed[name].items.map((item) => (
            <label key={item.label} className={`${field} grid items-center gap-2 sm:grid-cols-2`}>
              <span className="break-words">
                {item.label} <span className="font-normal">({item.count} saved rows)</span>
              </span>
              <select
                className={input}
                aria-label={`${name} label: ${item.label}`}
                value={Object.prototype.hasOwnProperty.call(values[`${name}_mapping`], item.label) ? values[`${name}_mapping`][item.label] : ""}
                onChange={(event) => {
                  const overrides = { ...values[`${name}_mapping`], [item.label]: event.target.value };
                  if (!event.target.value) delete overrides[item.label];
                  setValues({ ...values, [`${name}_mapping`]: overrides });
                }}
              >
                <option value="">Default: {item.default}{item.recognized ? "" : " (unrecognized label)"}</option>
                {(name === "status" ? ["open", "waiting", "blocked", "done", "cancelled"] : ["critical", "high", "normal", "low"]).map((value) => (
                  <option key={value} value={value}>{value}</option>
                ))}
              </select>
            </label>
          ))}
          {base.observed[name].remaining > 0 && (
            <p className="text-sm text-[#996617]">
              {base.observed[name].remaining} less frequent labels are not shown. Narrow the selected Sheet range to review those labels.
            </p>
          )}
        </fieldset>
      ))}
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>Save label mappings</button>
        <button type="button" className="secondary-button" disabled={busy} onClick={close}>Cancel</button>
      </div>
    </form>
  );
}

export default function TrackerPage() {
  const { sourceId } = useParams();
  return (
    <main className="app-page">
      <div className="app-page-inner max-w-6xl">
        {sourceId ? (
          <TrackerWorkspace key={sourceId} sourceId={sourceId} />
        ) : (
          <TrackerSources />
        )}
      </div>
    </main>
  );
}

export function TrackerWorkspace({ sourceId }) {
  const client = useQueryClient();
  const [view, setView] = useState("attention");
  const [text, setText] = useState("");
  const [offset, setOffset] = useState(0);
  const [writeOffset, setWriteOffset] = useState(0);
  const [followupOffset, setFollowupOffset] = useState(0);
  const [before, setBefore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState("");
  const [notice, setNotice] = useState("");
  const [noticeAction, setNoticeAction] = useState(null);
  const [editing, setEditing] = useState(null);
  const [followup, setFollowup] = useState(null);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [classificationOpen, setClassificationOpen] = useState(false);
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["tracker", sourceId, view, text, offset, writeOffset, followupOffset],
    queryFn: () =>
      trackerService.get(sourceId, {
        view,
        text,
        offset,
        write_offset: writeOffset,
        followup_offset: followupOffset,
      }),
    placeholderData: (previous) => previous,
    refetchInterval: 15000,
  });
  const history = useQuery({
    queryKey: ["tracker-history", sourceId, before],
    queryFn: () => trackerService.history(sourceId, before),
  });
  const run = async (operation) => {
    if (busy) return;
    setBusy(true);
    setFailure("");
    setNotice("");
    setNoticeAction(null);
    try {
      await operation();
      await Promise.all(
        [
          "tracker",
          "trackers",
          "tracker-history",
          "assistant",
          "today",
          "meeting",
        ].map((key) => client.invalidateQueries({ queryKey: [key] })),
      );
    } catch (reason) {
      if (reason.response?.status === 409)
        await client.invalidateQueries({ queryKey: ["tracker", sourceId] });
      const detail = reason.response?.data?.detail;
      setFailure(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((item) => item.msg).join("; ")
            : "Could not complete the tracker action. Your entered values are retained.",
      );
    } finally {
      setBusy(false);
    }
  };
  if (isLoading) return <p role="status">Loading tracker…</p>;
  if (error || !data)
    return (
      <div role="alert">
        Tracker unavailable.{" "}
        <button className="secondary-button" onClick={() => refetch()}>
          Retry tracker
        </button>
      </div>
    );
  return (
    <div className="space-y-5">
      <Link className="text-sm font-semibold text-[#246b52]" to="/trackers">
        ← Work trackers
      </Link>
      <header className="page-header">
        <div>
          <p className="eyebrow">Team follow-through</p>
          <h1 className="page-title">{data.source.title}</h1>
          <p className="page-description">
            {data.source.range} · Last synced{" "}
            {time(data.source.synced_at, data.timezone)}
          </p>
        </div>
        <button
          className="primary-button"
          disabled={busy}
          onClick={() => run(() => assistantService.sync(sourceId))}
        >
          Sync tracker
        </button>
      </header>
      {failure && (
        <p role="alert" className="surface-panel p-4 text-[#a14d38]">
          {failure}
        </p>
      )}
      {notice && (
        <p role="status" className="surface-panel p-4 text-[#246b52]">
          {notice}{" "}
          <Link className="font-semibold underline" to={`/assistant?view=outbox${noticeAction ? `&action=${noticeAction}` : ''}`}>
            Review in Outbox
          </Link>
        </p>
      )}
      {(data.source.error || data.source.stale) && (
        <p className="surface-panel p-4 text-sm text-[#996617]">
          {data.source.error ||
            "This source has not synced in the last 30 minutes. Refresh before relying on its status."}{" "}
          Automatic escalation drafts wait for a fresh successful sync.
        </p>
      )}
      {!!data.unconfirmed_followups && <p role="status" className="surface-panel p-4 text-sm text-[#996617]">
        {data.unconfirmed_followups} tracker follow-up {data.unconfirmed_followups === 1 ? 'has' : 'have'} an unconfirmed send result.
        {' '}Check the follow-up records below. Further messages for the same row are paused until the previous result is confirmed.
      </p>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {Object.entries(data.counts).map(([key, value]) => (
          <div key={key} className="surface-panel p-4">
            <p className="text-xl font-bold">{value}</p>
            <p className="text-xs capitalize text-[#68746d]">{key}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        {data.classification && (
          <button className="secondary-button" disabled={busy} onClick={() => setClassificationOpen(true)}>
            Status and priority labels
          </button>
        )}
        <button
          className="secondary-button"
          onClick={() => setPolicyOpen(true)}
        >
          Escalation draft settings
        </button>
        <Link className="secondary-button" to="/assistant?view=outbox">
          Open Outbox
        </Link>
        <a
          className="secondary-button"
          href={`https://docs.google.com/spreadsheets/d/${encodeURIComponent(data.source.external_id)}/edit`}
          target="_blank"
          rel="noreferrer"
        >
          Open Google Sheet
        </a>
      </div>
      {data.classification?.unmapped_values > 0 && (
        <p className="surface-panel p-4 text-sm text-[#996617]">
          {data.classification.unmapped_values} saved status or priority values use unrecognized labels.
          Review Status and priority labels to check their meaning. Unrecognized statuses
          currently count as open; unrecognized priorities count as normal.
        </p>
      )}
      <p className="text-xs text-[#68746d]">
        Automatic drafts: {data.policy.automatic_drafts ? "on" : "off"}.
        Background monitoring must be enabled in Assistant settings to keep
        syncing while you are away.
      </p>
      {policyOpen && (
        <PolicyForm
          initial={data.policy}
          busy={busy}
          close={() => setPolicyOpen(false)}
          save={(values) =>
            run(async () => {
              await trackerService.policy(sourceId, values);
              setPolicyOpen(false);
            })
          }
        />
      )}
      {classificationOpen && data.classification && (
        <ClassificationForm
          initial={data.classification}
          busy={busy}
          close={() => setClassificationOpen(false)}
          save={(values) => run(async () => {
            await trackerService.classification(sourceId, values);
            setClassificationOpen(false);
            setEditing(null);
            setFollowup(null);
            setOffset(0);
          })}
        />
      )}
      <section className="space-y-3" aria-label="Tracker work items">
        <div className="flex flex-wrap items-end gap-3">
          <label className={field}>
            Work view
            <select
              className={input}
              value={view}
              onChange={(event) => {
                setView(event.target.value);
                setOffset(0);
              }}
            >
              <option value="attention">Needs attention</option>
              <option value="open">All open work</option>
              <option value="all">All including closed</option>
            </select>
          </label>
          <label className={`${field} flex-1`}>
            Find by title, owner or ID
            <input
              className={input}
              maxLength={180}
              value={text}
              onChange={(event) => {
                setText(event.target.value);
                setOffset(0);
              }}
            />
          </label>
        </div>
        {editing && (
          <UpdateForm
            key={editing.id}
            row={editing}
            busy={busy}
            close={() => setEditing(null)}
            save={(values) =>
              run(async () => {
                await trackerService.prepare(sourceId, values);
                setEditing(null);
                setWriteOffset(0);
              })
            }
          />
        )}
        {followup && (
          <FollowupForm
            key={followup.id}
            row={followup}
            busy={busy}
            close={() => setFollowup(null)}
            save={(values) =>
              run(async () => {
                const action = await trackerService.followup(sourceId, followup.id, values);
                setFollowup(null);
                setNoticeAction(action.id);
                setNotice("Follow-up saved as an unsent draft.");
              })
            }
          />
        )}
        {!data.rows.length && (
          <p className="surface-panel p-5 text-sm">
            No work matches this view. Sync the source or change the filters.
          </p>
        )}
        {data.rows.map((row) => (
          <article className="surface-panel space-y-3 p-5" key={row.id}>
            <div className="flex flex-wrap justify-between gap-2">
              <h2 className="font-bold">
                {row.external_id} · {row.title}
              </h2>
              <span className="status-pill">
                {row.priority} · {row.status}
              </span>
            </div>
            <p className="text-sm">
              {row.owner || "Owner to confirm"} ·{" "}
              {row.due_at
                ? time(row.due_at, data.timezone)
                : "Deadline to confirm"}
            </p>
            <p className="text-xs text-[#68746d]">
              Source status: {row.values.status || "Not provided"} · Source
              priority: {row.values.priority || "Not provided"}
            </p>
            {row.signals.length > 0 && (
              <p className="text-sm text-[#996617]">
                {row.signals.join(" · ")}
              </p>
            )}
            <p className="text-xs text-[#68746d]">
              Last observed change:{" "}
              {time(row.details.last_changed_at, data.timezone)}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                className="secondary-button text-xs"
                disabled={
                  busy ||
                  !!data.source.error ||
                  row.details.missing_from_latest_sync ||
                  !row.editable_fields.length
                }
                onClick={() => setEditing(row)}
              >
                Propose source update
              </button>
              {!["done", "cancelled"].includes(row.status) && (
                <button
                  className="secondary-button text-xs disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={
                    busy ||
                    !!data.source.error ||
                    row.followup_pending ||
                    row.details.missing_from_latest_sync
                  }
                  onClick={() => setFollowup(row)}
                >
                  Prepare owner follow-up
                </button>
              )}
            </div>
            {row.followup_pending && <p className="mt-3 text-sm text-[#996617]">Check this row’s previous send in the follow-up records below before preparing another message.</p>}
          </article>
        ))}
        <div className="flex gap-2">
          {offset > 0 && (
            <button
              className="secondary-button"
              onClick={() => setOffset(Math.max(0, offset - 30))}
            >
              Previous items
            </button>
          )}
          {data.next_offset !== null && (
            <button
              className="secondary-button"
              onClick={() => setOffset(data.next_offset)}
            >
              More items
            </button>
          )}
        </div>
      </section>
      <section className="space-y-3" aria-label="Tracker follow-ups">
        <h2 className="text-lg font-bold">Follow-up drafts and receipts</h2>
        <p className="text-sm text-[#68746d]">Unconfirmed sends appear first, then drafts and recent history. Provider confirmation does not prove recipient delivery or completion of the work.</p>
        {!data.followups?.length && <p className="text-sm text-[#68746d]">No follow-ups prepared yet.</p>}
        {data.followups?.map(action => <article key={action.id} className="surface-panel space-y-2 p-5">
          <h3 className="font-bold">{action.external_id} · {action.title}</h3>
          <p className="text-sm">{action.status === 'sent' ? 'Provider confirmed' : ['sending', 'needs_review'].includes(action.status) ? 'Send result unconfirmed' : action.status === 'draft' ? 'Unsent draft' : action.status.replaceAll('_', ' ')}</p>
          <p className="text-xs text-[#68746d]">{action.recipients.join(', ')} · Prepared {time(action.created_at, data.timezone)}</p>
          <Link className="text-sm font-semibold underline" to={`/assistant?view=outbox&action=${action.id}`}>Review follow-up in Outbox</Link>
        </article>)}
        <div className="flex gap-2">
          {followupOffset > 0 && <button className="secondary-button" onClick={() => setFollowupOffset(Math.max(0, followupOffset - 20))}>Previous follow-ups</button>}
          {data.next_followup_offset != null && <button className="secondary-button" onClick={() => setFollowupOffset(data.next_followup_offset)}>More follow-ups</button>}
        </div>
      </section>
      <section className="space-y-3" aria-label="Reviewed tracker updates">
        <h2 className="text-lg font-bold">Reviewed Sheet updates</h2>
        <p className="text-sm text-[#68746d]">
          Applying changes writes to the marked source row and verifies the
          result. Coordinate simultaneous edits to these cells; Sheets does not
          provide an atomic check-and-write. Formula cells are protected.
        </p>
        {!data.writes.length && (
          <p className="text-sm text-[#68746d]">No updates prepared yet.</p>
        )}
        {data.writes.map((write) => (
          <article key={write.id} className="surface-panel space-y-3 p-5">
            <div className="flex justify-between gap-2">
              <h3 className="font-bold">
                {write.external_id} · {write.title}
              </h3>
              <span className="status-pill">
                {write.status.replaceAll("_", " ")}
              </span>
            </div>
            <Changes before={write.before} after={write.changes} />
            {write.result.message && (
              <p role="status" className="text-sm">
                {write.result.message}
              </p>
            )}
            {write.result.observed && (
              <details>
                <summary className="cursor-pointer text-sm">
                  Current source values
                </summary>
                <Changes before={{}} after={write.result.observed} />
              </details>
            )}
            {write.status === "draft" && (
              <div className="flex gap-2">
                <button
                  className="primary-button"
                  disabled={busy}
                  onClick={() => run(() => trackerService.apply(write.id))}
                >
                  Apply to Google Sheets
                </button>
                <button
                  className="secondary-button"
                  disabled={busy}
                  onClick={() => run(() => trackerService.cancel(write.id))}
                >
                  Cancel update
                </button>
              </div>
            )}
            {["needs_review", "applying"].includes(write.status) && (
              <button
                className="secondary-button"
                disabled={busy}
                onClick={() => run(() => trackerService.reconcile(write.id))}
              >
                Check source result
              </button>
            )}
          </article>
        ))}
        <div className="flex gap-2">
          {writeOffset > 0 && (
            <button
              className="secondary-button"
              onClick={() => setWriteOffset(Math.max(0, writeOffset - 20))}
            >
              Newer updates
            </button>
          )}
          {data.next_write_offset !== null && (
            <button
              className="secondary-button"
              onClick={() => setWriteOffset(data.next_write_offset)}
            >
              Older updates
            </button>
          )}
        </div>
      </section>
      <section className="space-y-3" aria-label="Observed tracker changes">
        <h2 className="text-lg font-bold">Observed changes</h2>
        <p className="text-xs text-[#68746d]">
          Recorded when Timelyzer syncs. This is not the sheet’s complete edit
          history or proof of when a developer made a change.
        </p>
        {history.error && (
          <p role="alert">
            Change history could not load.{" "}
            <button onClick={() => history.refetch()}>Retry history</button>
          </p>
        )}
        {history.data?.items.map((change) => (
          <details key={change.id} className="surface-panel p-4">
            <summary className="cursor-pointer text-sm font-semibold">
              {change.external_id} · {change.title} ·{" "}
              {time(change.created_at, data.timezone)}
            </summary>
            <Changes before={change.before} after={change.after} />
          </details>
        ))}
        <div className="flex gap-2">
          {before && (
            <button
              className="secondary-button"
              onClick={() => setBefore(null)}
            >
              Latest changes
            </button>
          )}
          {history.data?.next_before_id && (
            <button
              className="secondary-button"
              onClick={() => setBefore(history.data.next_before_id)}
            >
              Older changes
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
